import React, { useState } from "react";
import {
  CartAddBodyWithTracking,
  shopifyCartApi,
} from "@/services/shopifyCart";
import { useCartUiActions } from "@/features/cart/cartStore";
import { CoverImage } from "@/components/CoverImage";
import { Button } from "@/components/Button";
import { RadioCards } from "@/components/RadioCards";
import { Collapse } from "@mui/material";
import { SvgSrcIcon } from "@/components/SvgSrcIcon";
import { ProductCard } from "@/features/product/ProductCard";
import { ProductScent } from "@/features/product/ProductScent";
import { $ } from "@/helpers/Money";
import { productSelect } from "@/store";
import { check } from "@/helpers/utils";

const recommendations = [
  {
    prompt: "I need clean clothes",
    recommendation: "We recommend these\nlaundry detergent pods",
    icon: {
      src: "//cdn.shopify.com/s/files/1/0268/4219/files/product-Laundry-iconfill-circle-Colorized.svg",
      width: 48,
      height: 48,
    },
    product: {
      title: "Stain & Odor, Clean & Crisp",
      featuredText:
        "Our best detergent for everyday deep cleaning and longlasting freshness.",
      size: "64 Pods",
      price: $`26`,
      featuredImage: {
        src: "//cdn.shopify.com/s/files/1/0268/4219/products/StainOdor-CleanAndCrisp_b2afa38f-0211-48a9-a5cd-f1f5d263e26b.jpg?v=1616043170",
      },
      url: "/products/stain-odor-clean-and-crisp-laundry-detergent-pods",
      variantId: 39717213536355,
    },
  },
  {
    prompt: "I need clean dishes",
    recommendation: "We recommend these\ndishwasher detergent pods",
    icon: {
      src: "//cdn.shopify.com/s/files/1/0268/4219/files/product-Dishwasher-iconfill-circle-colorized.svg",
      width: 48,
      height: 48,
    },
    product: {
      title: "Dishwasher Detergent, Lemon",
      featuredText: "Helps remove stuck on food and tough greasy residue.",
      size: "64 Pods",
      price: $`25`,
      featuredImage: {
        src: "//cdn.shopify.com/s/files/1/0268/4219/products/dishwasher-lemon.jpg?v=1605581517",
      },
      url: "/products/lemon-dishwasher-detergent-pods",
      variantId: 37970405074,
    },
  },
  {
    prompt: "I need a bleach alternative",
    recommendation: "We recommend this product",
    icon: {
      src: "//cdn.shopify.com/s/files/1/0268/4219/files/product-OxiBoost-iconfill-circle-colorized.svg",
      width: 48,
      height: 48,
    },
    product: {
      title: "Oxi Booster, Unscented",
      featuredText: "Color-safe bleach alternative.",
      size: "64 Pods",
      price: $`25`,
      featuredImage: {
        src: "//cdn.shopify.com/s/files/1/0268/4219/products/oxi-unscented.jpg?v=1605581862",
      },
      url: "/products/oxi-booster-pods",
      variantId: 4020376502299,
    },
  },
];

export const ProductDiscovery = () => {
  const [selected, setSelected] = useState<number | undefined>(undefined);
  const [showRecommendation, setShowRecommendation] = useState(false);
  const addItemsToCart = shopifyCartApi.useTrackedAdd();
  const cartActions = useCartUiActions();

  const handleChange = (value: number | undefined) => {
    if (value === undefined) {
      setShowRecommendation(false);
    } else {
      setSelected(value);
      setShowRecommendation(true);
    }
  };

  const options = recommendations.map((recommendation, index) => {
    return {
      label: (
        <div className="flex items-center sm:space-x-4 lg:flex-col lg:space-x-0 lg:space-y-4 lg:pl-8">
          <SvgSrcIcon
            src={recommendation.icon.src}
            height={recommendation.icon.height ?? 48}
            width={recommendation.icon.width ?? 48}
            className="svg-icon hidden shrink-0 transition duration-300 sm:block"
          ></SvgSrcIcon>
          <h5 className="title text-lg sm:text-xl lg:text-center lg:text-2xl">
            {recommendation.prompt}
          </h5>
        </div>
      ),
      value: index,
    };
  });
  return (
    <div className="page-module mx-auto max-w-3xl pt-0 lg:max-w-screen-xl">
      <div className="space-y-8">
        <h2 className="title text-center text-4xl font-bold text-primary">
          What are you looking for today?
        </h2>
        <RadioCards
          value={selected}
          options={options}
          onChange={handleChange}
          layout="responsive"
          clearable
        />
        <Collapse in={showRecommendation}>
          {selected !== undefined && (
            <div className="flex flex-col overflow-hidden rounded-lg border border-solid border-primary-200 bg-primary-50 lg:flex-row">
              <div className="hidden basis-6/12 lg:block">
                <CoverImage
                  src={recommendations[selected].product.featuredImage.src}
                />
              </div>
              <div className="flex basis-6/12 flex-col items-center justify-center p-4 lg:p-8">
                <div className="w-full space-y-8 text-center lg:max-w-sm">
                  <h3 className="subtitle whitespace-pre-line font-semibold">
                    {recommendations[selected].recommendation}
                  </h3>
                  <div className="mx-auto block max-w-sm lg:hidden">
                    <ProductCard
                      className="w-full shadow-lg"
                      productCardValue={recommendations[selected].product}
                      compact={true}
                    />
                  </div>
                  <div className="hidden space-y-2 lg:block">
                    <h4 className="title text-primary">
                      {productSelect.name(recommendations[selected].product)}
                    </h4>
                    <ProductScent
                      className="bg-white"
                      scentName={check(
                        productSelect.scent(recommendations[selected].product),
                        "product discovery requires scent"
                      )}
                    />
                    <p className="body">
                      {recommendations[selected].product.featuredText}
                    </p>
                    <p className="body text-sm font-semibold">
                      {recommendations[selected].product.size}
                    </p>
                    <p className="body text-xl font-semibold text-primary">
                      {recommendations[selected].product.price.format()}
                    </p>
                  </div>
                  <div className="flex w-full flex-col items-center space-y-4 sm:flex-row sm:justify-center sm:space-x-4 sm:space-y-0">
                    <Button
                      component="a"
                      href={recommendations[selected].product.url}
                      className="w-full sm:w-auto"
                      variant="outline"
                      aria-label={`Learn more about ${recommendations[selected].product.title}`}
                    >
                      Learn More
                    </Button>
                    <Button
                      className="w-full sm:w-auto"
                      onClick={() => {
                        const payload: CartAddBodyWithTracking = {
                          items: [
                            {
                              id: recommendations[selected].product.variantId,
                              quantity: 1,
                            },
                          ],
                          sourceModule: "product_discovery",
                        };
                        addItemsToCart.mutate(payload, {
                          onSuccess: () => cartActions.openDrawer(),
                        });
                      }}
                      loading={addItemsToCart.isLoading}
                      aria-label={`Add ${recommendations[selected].product.title} to cart`}
                    >
                      Add To Cart
                    </Button>
                  </div>
                </div>
              </div>{" "}
            </div>
          )}
        </Collapse>
      </div>
    </div>
  );
};
